import Box, { type BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { sxList, themeConfig } from '@/theme';

export type BrandMarkVariant = 'color' | 'white' | 'ink';

type MarkProps = {
  size?: number;
  /**
   * `color`: V em Volt Blue e D na cor do texto — Ink no claro, branco no escuro, como o
   * guia pede para fundos escuros. `white`: marca toda branca, para superfícies Volt Blue.
   * `ink`: monocromática.
   */
  variant?: BrandMarkVariant;
} & Omit<BoxProps<'svg'>, 'component'>;

/**
 * O símbolo VD, desenhado pela grade do Brand Guidelines (seção 1): T é a espessura do
 * traço e a unidade do `viewBox`; o símbolo ocupa 4T × 4T. O D é um meio-anel de raio
 * externo 2T; o V, dois braços a 45° com alcance horizontal de 2T e pontas arredondadas.
 * O V é desenhado por cima, e suas pontas redondas cobrem as extremidades retas do D. É o
 * mesmo desenho de `public/favicon.svg` — mudou aqui, muda lá.
 */
export function BrandMark({ size = 40, variant = 'color', sx, ...props }: MarkProps) {
  const chevron =
    variant === 'color'
      ? themeConfig.brand.voltBlue
      : variant === 'white'
        ? '#FFFFFF'
        : 'currentColor';
  const bowl = variant === 'white' ? '#FFFFFF' : 'currentColor';

  return (
    <Box
      component="svg"
      viewBox="-0.06 -0.06 4.12 4.12"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      sx={[
        {
          display: 'block',
          flexShrink: 0,
          color: variant === 'ink' ? themeConfig.brand.ink : 'inherit',
        },
        ...sxList(sx),
      ]}
      {...props}
    >
      <path d="M2 0.5A1.5 1.5 0 0 1 2 3.5" fill="none" stroke={bowl} strokeWidth="1" />
      <path
        d="M2 0.5L0.5 2L2 3.5"
        fill="none"
        stroke={chevron}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

type LogoProps = {
  /** Só o símbolo, para barras estreitas. */
  compact?: boolean;
  height?: number;
  to?: string;
  variant?: BrandMarkVariant;
};

/**
 * A versão horizontal da marca: símbolo + wordmark "VincDev" em Space Grotesk 700, com a
 * área de proteção de um traço entre os dois.
 */
export function Logo({ compact = false, height = 36, to = '/', variant = 'color' }: LogoProps) {
  return (
    <Box
      component={RouterLink}
      to={to}
      aria-label="VincDev — página inicial"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${height * 0.28}px`,
        textDecoration: 'none',
        color: variant === 'white' ? 'common.white' : 'text.primary',
      }}
    >
      <BrandMark size={height} variant={variant} />
      {!compact && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontFamily: theme.typography.fontSecondaryFamily,
            fontWeight: 700,
            fontSize: height * 0.6,
            lineHeight: 1,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
          })}
        >
          VincDev
        </Typography>
      )}
    </Box>
  );
}
